import { createContext, useContext, useMemo, useState } from "react";

const STORAGE_KEY = "consensus-ai-language";

export const LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "es", label: "Espanol" },
  { code: "fr", label: "Francais" },
  { code: "pt", label: "Portugues" },
];

const DICTIONARY = {
  en: {
    "nav.home": "Home",
    "nav.chat": "AI Chat",
    "nav.marketing": "Marketing",
    "nav.image": "Image Gen",
    "nav.imageStudio": "Image Studio",
    "nav.video": "Video Gen",
    "nav.tts": "Text to Speech",
    "nav.studio": "Video Studio",
    "nav.shortsStudio": "Shorts Studio",
    "nav.model3d": "3D Model Studio",
    "nav.code": "Code Editor",
    "nav.about": "About",
    "common.soon": "Soon",
    "layout.health.ready": "NVIDIA NIM ready",
    "layout.health.missing": "NIM key missing",
    "layout.language.label": "Language",
    "home.welcome": "Welcome to",
    "home.hero": "Tell us what you want to make — chat, images, code, speech, YouTube Shorts, and more. We'll open the right tool and start working on it.",
    "home.free": "100% free and unlimited A.I. access- no sign up, no subscription needed",
    "home.ask": "What do you want to create?",
    "home.placeholder": "e.g. Generate an image of a sunset, explain transformers, or build a React app…",
    "home.starting": "Starting…",
    "home.go": "Go",
    "home.opens": "Opens",
    "home.comingSoon": "(coming soon)",
    "home.tip": "Tip: Cmd/Ctrl + Enter to start",
    "home.pick": "Or pick a tool",
    "home.tellFriend": "Tell a friend",
    "home.share.text": "Text",
    "home.share.email": "Email",
    "home.share.x": "X Post",
    "home.share.linkedin": "LinkedIn",
    "home.share.copy": "Copy Link",
    "home.share.copied": "Copied",
    "home.feature.chat.title": "AI Chat",
    "home.feature.chat.desc": "Have a conversation with a powerful AI assistant.",
    "home.feature.marketing.title": "Marketing",
    "home.feature.marketing.desc": "Generate social media post copy and matching images for your business.",
    "home.feature.image.title": "AI Image Generation",
    "home.feature.image.desc": "Turn a written description into a brand new image.",
    "home.feature.imageStudio.title": "Image Studio",
    "home.feature.imageStudio.desc": "Layer images and add styled text overlays for social posts and ads.",
    "home.feature.video.title": "AI Video Generation",
    "home.feature.video.desc": "Video generation is temporarily unavailable while we upgrade this workspace.",
    "home.feature.tts.title": "Text to Speech",
    "home.feature.tts.desc": "Turn any text into spoken audio with neural voices.",
    "home.feature.studio.title": "Video Studio",
    "home.feature.studio.desc": "Edit scenes with images, video, audio, and AI captions.",
    "home.feature.shorts.title": "Shorts Studio",
    "home.feature.shorts.desc": "Upload your own video clips and turn them into short-video concepts like rankings and explainers.",
    "home.feature.model3d.title": "3D Model Studio",
    "home.feature.model3d.desc": "CAD mode: describe any part → GLB + STL. Game mode: AI image → GLB.",
    "model3d.title": "3D Model Studio",
    "model3d.subtitleCad": "Describe any printable part in plain language. AI writes CadQuery and exports GLB (STL included for printing).",
    "model3d.subtitleGame": "Game mode generates a real 3D mesh GLB — TRELLIS when available, otherwise a volumetric relief model from your image.",
    "model3d.cadMode": "CAD mode",
    "model3d.gameMode": "Game asset mode",
    "model3d.cadPrompt": "CAD part description",
    "model3d.gamePrompt": "Game asset description",
    "model3d.referenceImage": "Reference image",
    "model3d.quality": "Mesh quality",
    "model3d.seed": "Image seed (0 = random)",
    "model3d.tryPresets": "Try a preset",
    "model3d.generating": "Generating 3D model…",
    "model3d.generateCad": "Generate CAD GLB",
    "model3d.generateGame": "Generate game GLB",
    "model3d.noteCad": "CAD mode exports GLB for preview and STL for 3D printing via CadQuery. Python 3 + CadQuery + trimesh must be installed on the server (server/python/requirements.txt).",
    "model3d.noteGame": "Game mode tries NVIDIA TRELLIS for full 3D meshes, then falls back to a local volumetric relief model from your image.",
    "model3d.preview": "Preview",
    "model3d.download": "Download",
    "shorts.uploadNoteTitle": "Shorts Studio upload workflow",
    "shorts.uploadNoteBody": "Download YouTube videos yourself with your preferred tool, upload the clip here, and AI will transform it into short-form variants.",
    "home.feature.code.title": "AI Code Editor",
    "home.feature.code.desc": "Write, edit, and explain code with an AI pair programmer.",
    "home.otherProducts.title": "Try some of our other products",
    "home.otherProducts.lucid.title": "Lucid Audio",
    "home.otherProducts.lucid.desc": "Stem mixing studio for streaming and music production.",
    "home.otherProducts.text2cad.title": "A.I. Text 2 C.A.D.",
    "home.otherProducts.text2cad.desc": "Turn plain-language descriptions into 3D CAD models.",
    "home.otherProducts.rna.title": "RNA Code",
    "home.otherProducts.rna.desc": "AI-powered coding tools and development resources.",
    "home.mobileApp.title": "Try our mobile app",
    "home.mobileApp.desc":
      "Get Consensus A.I. on iPhone and iPad — AI chat with text and speech input, text and audio responses, and support for top models.",
    "home.mobileApp.cta": "Download on the App Store",
    "about.subtitle": "What The Consensus A.I. is and what you can do here.",
    "about.paragraph": "The Consensus A.I. is a free, all-in-one AI studio on the web. Today you can use Chat, Marketing, Image Generation, Image Studio, Text to Speech, Video Studio, Shorts Studio, 3D Model Studio, and the Code Editor with no sign-up required. Video Generation is currently being rebuilt and marked coming soon.",
    "about.shortsUploadNote": "For Shorts Studio: download YouTube videos on your own, upload clips directly, and let AI generate short variants with captions and smart framing.",
    "about.values.title": "What we believe",
    "about.values.free.title": "100% free and unlimited",
    "about.values.free.desc": "No sign-in and no subscription needed. Open any tool and create right away.",
    "about.values.powered.title": "Powered by NVIDIA NIM",
    "about.values.powered.desc": "Models run through NVIDIA's inference stack for fast, reliable chat, vision, speech, and code.",
    "about.values.makers.title": "Built for makers",
    "about.values.makers.desc": "From quick experiments to deployable projects — download your code and take it anywhere.",
    "about.tools.title": "Tools in the studio",
    "about.ready.title": "Ready to create?",
    "about.ready.desc": "Head to the home page to pick a tool, or jump straight into Chat, Shorts Studio, Marketing, Image Generation, or the Code Editor. Everything runs in your browser — no account required.",
    "about.ready.home": "Go to Home",
    "about.ready.chat": "Start chatting",
    "comingSoon.availableNow": "Chat, code, speech, and Video Studio are available now.",
    "comingSoon.backHome": "Back to home",
    "video.unavailable": "Video generation is temporarily unavailable.",
    "video.rebuilding": "We are rebuilding this workspace and will bring back video generation in a future update.",
    "common.starting": "Starting…",
    "common.stop": "Stop",
    "common.send": "Send",
    "common.download": "Download",
    "common.model": "Model",
    "common.prompt": "Prompt",
    "common.size": "Size",
    "common.seed": "Seed",
    "common.generating": "Generating…",
    "common.generate": "Generate",
    "common.save": "Save",
    "common.remove": "Remove",
    "common.moveUp": "Move up",
    "common.moveDown": "Move down",
    "common.delete": "Delete",
    "common.exporting": "Exporting...",
    "common.reset": "Reset",
    "common.clear": "Clear",
    "common.copy": "Copy",
    "common.run": "Run",
    "common.edit": "Edit",
    "common.explain": "Explain",
    "common.thinking": "Thinking",
    "common.view": "view",
    "common.text": "Text",
    "chat.subtitle": "Stream responses from frontier open models via NVIDIA NIM.",
    "chat.clear": "Clear conversation",
    "chat.placeholder": "Message The Consensus A.I. — Shift+Enter for newline",
    "chat.systemPrompt": "System prompt",
    "chat.copyMessage": "Copy message",
    "chat.emptyTitle": "Ask The Consensus anything",
    "chat.emptySubtitle": "Open-weight frontier models, streamed live through NVIDIA NIM.",
    "marketing.subtitle": "Create social post copy with chat AI and matching visuals in one workflow.",
    "marketing.hint": "Copy usually streams in seconds. Images may take 5–30 seconds.",
    "marketing.business": "Business",
    "marketing.businessPlaceholder": "e.g. Pine & Stone Dental",
    "marketing.offer": "Offer / Service",
    "marketing.offerPlaceholder": "e.g. New patient cleaning special",
    "marketing.audience": "Target audience",
    "marketing.audiencePlaceholder": "e.g. Busy parents in Austin",
    "marketing.platform": "Platform",
    "marketing.tone": "Tone",
    "marketing.goal": "Goal",
    "marketing.goalPlaceholder": "e.g. Book appointments this week",
    "marketing.notes": "Extra notes",
    "marketing.notesPlaceholder": "Any promo details, brand voice notes, or seasonal angle",
    "marketing.chatModel": "Chat model",
    "marketing.imageModel": "Image model",
    "marketing.generateCopy": "Generate copy",
    "marketing.generateAll": "Generate post + image",
    "marketing.postDraft": "Post draft",
    "marketing.copyPost": "Copy post",
    "marketing.regenerateCopy": "Regenerate copy",
    "marketing.captionPlaceholder": "Your generated social caption will appear here.",
    "marketing.hashtags": "Hashtags",
    "marketing.hashtagsPlaceholder": "#smallbusiness #localbusiness #marketing",
    "marketing.creativeImage": "Creative image",
    "marketing.generateImage": "Generate image",
    "marketing.imagePromptPlaceholder": "Image concept prompt for your post",
    "marketing.imageOutputPlaceholder": "Generated post image appears here.",
    "marketing.status.copy": "Writing post copy…",
    "marketing.status.image": "Generating post image…",
    "marketing.copyError": "Could not copy to clipboard. Please copy manually.",
    "image.subtitle": "Generate from a text prompt with NVIDIA NIM, Together AI, and Pollinations.",
    "image.hint": "Usually takes 5–30 seconds. If rate-limited, wait ~15s and retry.",
    "image.promptPlaceholder": "Describe the image you want…",
    "image.referenceImage": "Reference image",
    "image.switchForReference": "Switch to SD3 to use this",
    "image.uploadReference": "Click to upload a reference image",
    "image.notSupported": "Not supported by current model",
    "image.strength": "Strength",
    "image.keepInput": "Keep input",
    "image.followPrompt": "Follow prompt",
    "image.negativePrompt": "Negative prompt",
    "image.negativePlaceholder": "What to avoid (optional)",
    "image.randomSeed": "Random seed",
    "image.painting": "Painting pixels…",
    "image.emptyTitle": "Your gallery is empty",
    "image.emptyBody": "Describe an image on the left and hit Generate. Results appear here.",
    "imageStudio.canvasUnavailable": "Canvas rendering is not available in this browser.",
    "imageStudio.exportFailed": "Failed to export PNG.",
    "imageStudio.exportPng": "Export PNG",
    "imageStudio.addImageLayer": "Add image layer",
    "imageStudio.addTextOverlay": "Add text overlay",
    "imageStudio.canvas": "Canvas",
    "imageStudio.layers": "Layers",
    "imageStudio.imageLayer": "Image layer",
    "imageStudio.textOverlay": "Text overlay",
    "imageStudio.hideLayer": "Hide layer",
    "imageStudio.showLayer": "Show layer",
    "imageStudio.selectedLayer": "Selected Layer",
    "imageStudio.newCaption": "New caption",
    "imageStudio.newTextOverlay": "New text overlay",
    "imageStudio.preset.square": "Square",
    "imageStudio.preset.portrait": "Portrait",
    "imageStudio.preset.landscape": "Landscape",
    "imageStudio.x": "X (%)",
    "imageStudio.y": "Y (%)",
    "imageStudio.opacity": "Opacity",
    "imageStudio.rotation": "Rotation",
    "imageStudio.width": "Width (%)",
    "imageStudio.height": "Height (%)",
    "imageStudio.fontSize": "Font size",
    "imageStudio.weight": "Weight",
    "imageStudio.letterSpacing": "Letter spacing",
    "imageStudio.color": "Color",
    "imageStudio.alignment": "Alignment",
    "imageStudio.align.left": "Left",
    "imageStudio.align.center": "Center",
    "imageStudio.align.right": "Right",
    "tts.subtitle": "Turn text into spoken audio — instantly in your browser, or with NVIDIA's neural voices.",
    "tts.textPlaceholder": "Type or paste the text you want to hear out loud…",
    "tts.browserVoice": "Browser voice (instant)",
    "tts.nvidiaVoice": "NVIDIA neural (downloadable)",
    "tts.speak": "Speak",
    "tts.synthesizing": "Synthesizing…",
    "tts.generateAudio": "Generate audio",
    "tts.downloadWav": "Download WAV",
    "tts.voice": "Voice",
    "tts.noWebSpeech": "Your browser doesn't support Web Speech. Use the NVIDIA engine on the right instead.",
    "tts.loadingVoices": "(loading voices…)",
    "tts.voiceNote": "Voices come from your OS. macOS & iOS have great built-in natural voices.",
    "tts.rate": "Rate",
    "tts.pitch": "Pitch",
    "tts.defaultVoice": "default",
    "tts.tryVoicesPrefix": "Try",
    "tts.nvidiaNote": "NVIDIA neural TTS may not be enabled on every account. If generation fails, use the browser voice — it works everywhere.",
    "code.subtitle": "Single-file scratchpad, React websites, or React Native mobile apps.",
    "code.toggleConsole": "Toggle console",
    "code.console": "Console",
    "code.openSnack": "Open in Expo Snack",
    "code.hideAiAssistant": "Hide AI assistant",
    "code.showAiAssistant": "Show AI assistant",
    "code.hideAi": "Hide AI",
    "code.showAi": "Show AI",
    "code.appName": "App name",
    "code.appSettings": "App settings",
    "code.iosBundle": "iOS bundle ID",
    "code.androidPackage": "Android package",
    "code.appIcon": "App icon",
    "code.viewExpoGo": "Expo Go",
    "code.tryExpoGo": "Try on your device with Expo Go",
    "code.refreshQr": "Refresh QR",
    "code.openSnackBrowser": "Open Snack in browser",
    "code.askAi": "Ask AI",
    "code.tipSubmit": "Tip: Cmd/Ctrl + Enter to submit",
  },
  es: {
    "nav.home": "Inicio",
    "nav.chat": "Chat IA",
    "nav.marketing": "Marketing",
    "nav.image": "Generador de imagenes",
    "nav.imageStudio": "Estudio de imagen",
    "nav.video": "Generador de video",
    "nav.tts": "Texto a voz",
    "nav.studio": "Estudio de video",
    "nav.code": "Editor de codigo",
    "nav.about": "Acerca de",
    "common.soon": "Pronto",
    "layout.health.ready": "NVIDIA NIM listo",
    "layout.health.missing": "Falta clave de NIM",
    "layout.language.label": "Idioma",
    "home.welcome": "Bienvenido a",
    "home.hero": "Dinos que quieres crear: chat, imagenes, codigo, voz y mas. Abriremos la herramienta correcta.",
    "home.free": "100% gratis y acceso ilimitado de I.A.- sin registro y sin suscripcion",
    "home.ask": "Que quieres crear?",
    "home.starting": "Iniciando…",
    "home.go": "Ir",
    "home.pick": "O elige una herramienta",
    "home.tellFriend": "Compartir con un amigo",
    "home.share.text": "Texto",
    "home.share.email": "Correo",
    "home.share.copy": "Copiar enlace",
    "home.share.copied": "Copiado",
    "about.subtitle": "Que es The Consensus A.I. y que puedes hacer aqui.",
    "about.values.title": "En que creemos",
    "about.tools.title": "Herramientas del estudio",
    "about.ready.title": "Listo para crear?",
    "about.ready.home": "Ir a inicio",
    "about.ready.chat": "Comenzar chat",
  },
  fr: {
    "nav.home": "Accueil",
    "nav.chat": "Chat IA",
    "nav.marketing": "Marketing",
    "nav.image": "Generation d'image",
    "nav.imageStudio": "Studio d'image",
    "nav.video": "Generation video",
    "nav.tts": "Texte en parole",
    "nav.studio": "Studio video",
    "nav.code": "Editeur de code",
    "nav.about": "A propos",
    "common.soon": "Bientot",
    "layout.health.ready": "NVIDIA NIM pret",
    "layout.health.missing": "Cle NIM manquante",
    "layout.language.label": "Langue",
    "home.welcome": "Bienvenue sur",
    "home.hero": "Dites-nous ce que vous voulez creer : chat, images, code, voix et plus.",
    "home.free": "100% gratuit et acces IA illimite- sans inscription ni abonnement",
    "home.ask": "Que voulez-vous creer ?",
    "home.starting": "Demarrage…",
    "home.go": "Go",
    "home.pick": "Ou choisissez un outil",
    "home.tellFriend": "Parler a un ami",
    "home.share.text": "SMS",
    "home.share.email": "Email",
    "home.share.copy": "Copier le lien",
    "home.share.copied": "Copie",
    "about.subtitle": "Ce qu'est The Consensus A.I. et ce que vous pouvez faire ici.",
    "about.values.title": "Nos convictions",
    "about.tools.title": "Outils du studio",
    "about.ready.title": "Pret a creer ?",
    "about.ready.home": "Aller a l'accueil",
    "about.ready.chat": "Commencer le chat",
  },
  pt: {
    "nav.home": "Inicio",
    "nav.chat": "Chat IA",
    "nav.marketing": "Marketing",
    "nav.image": "Geracao de imagem",
    "nav.imageStudio": "Estudio de imagem",
    "nav.video": "Geracao de video",
    "nav.tts": "Texto para fala",
    "nav.studio": "Estudio de video",
    "nav.code": "Editor de codigo",
    "nav.about": "Sobre",
    "common.soon": "Em breve",
    "layout.health.ready": "NVIDIA NIM pronto",
    "layout.health.missing": "Chave NIM ausente",
    "layout.language.label": "Idioma",
    "home.welcome": "Bem-vindo ao",
    "home.hero": "Diga o que voce quer criar — chat, imagens, codigo, voz e mais.",
    "home.free": "100% gratis e acesso ilimitado de I.A.- sem cadastro e sem assinatura",
    "home.ask": "O que voce quer criar?",
    "home.starting": "Iniciando…",
    "home.go": "Ir",
    "home.pick": "Ou escolha uma ferramenta",
    "home.tellFriend": "Conte para um amigo",
    "home.share.text": "Texto",
    "home.share.email": "Email",
    "home.share.copy": "Copiar link",
    "home.share.copied": "Copiado",
    "about.subtitle": "O que e The Consensus A.I. e o que voce pode fazer aqui.",
    "about.values.title": "No que acreditamos",
    "about.tools.title": "Ferramentas do studio",
    "about.ready.title": "Pronto para criar?",
    "about.ready.home": "Ir para inicio",
    "about.ready.chat": "Iniciar chat",
  },
};

const I18nContext = createContext(null);

function getInitialLanguage() {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && LANGUAGE_OPTIONS.some((option) => option.code === stored)) {
    return stored;
  }
  return "en";
}

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = (nextLanguage) => {
    const normalized = LANGUAGE_OPTIONS.some((option) => option.code === nextLanguage)
      ? nextLanguage
      : "en";
    setLanguageState(normalized);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, normalized);
    }
  };

  const value = useMemo(() => {
    const t = (key, fallback) =>
      DICTIONARY[language]?.[key] ??
      DICTIONARY.en?.[key] ??
      fallback ??
      key;
    return { language, setLanguage, t };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return context;
}
